import React from "react";
import Typography from "@common/components/Typography";
import ActionIndicator from "./ActionIndicator";
import EntityTypeIndicator from "./EntityTypeIndicator";
import { Log } from "@services/audit/types";

export const LEFT_FIXED_SIZE = 320;
import { TFunction } from "react-i18next";
import {
  ColDefWithTab,
  TabValue,
} from "@pages/Assets/common/FilterColumn/context";
import ListTypography from "@common/components/ListDetailViews/ListView/ListTypography";
import {
  displayDate,
  displayDateTimeCSV,
  displayTime,
} from "@common/utils/localTime.utils";
import { addDevicesSourceFilter } from "@pages/Dashboard/WidgetEdit/FilterPanel/constants";
const DMCA_VALUE = "Device Management and Control Agent (DMCA)";

const reorderDMCA = (source: string): string => {
  const deviceSources = source.split(" & ");
  if (
    deviceSources.length === 2 &&
    deviceSources[1] === DMCA_VALUE &&
    deviceSources[0] !== "Symphony Direct"
  ) {
    return `${DMCA_VALUE} & ${deviceSources[0]}`;
  }
  return source;
};

const deviceSourceList = addDevicesSourceFilter.map((item) => {
  const correctedValue = item.value.replace(/DMCA/g, DMCA_VALUE);
  const value = reorderDMCA(correctedValue);
  return {
    ...item,
    value: value,
  };
});

const getNewMonitoringSourceLabel = (
  value?: string,
  fieldName?: string
): string => {
  if (fieldName !== "Monitoring Source") return value ?? "";
  return (
    deviceSourceList.find((deviceSource) => deviceSource.value === value)
      ?.label ??
    value ??
    ""
  );
};

export const getColumns = (
  t: TFunction<"translation", undefined>
): ColDefWithTab<Log>[] => {
  return [
    {
      key: "created",
      label: "Date Time",
      width: LEFT_FIXED_SIZE,
      sortable: true,
      render: (row, column) => (
        <Typography
          size="small"
          sx={{
            textAlign: "center",
            width: "100%",
          }}
        >
          {displayDate(row.created)}
          <br />
          {displayTime(row.created)}
        </Typography>
      ),
      tab: [TabValue.Export, TabValue.Info],
      csvValue: (row) => displayDateTimeCSV(Number(row.created)),
    },
    {
      label: "Username",
      key: "userName",
      sortable: true,
      render: ({ userName }) => (
        <ListTypography cellValue={userName} tooltip={userName} />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "User ID",
      key: "userId",
      tab: [TabValue.Export],
    },
    {
      label: "Object",
      key: "path",
      width: 480,
      sortable: true,
      render: ({ path }) => <ListTypography tooltip={path} cellValue={path} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Action",
      key: "action",
      width: 160,
      sortable: true,
      render: ({ action }) => <ActionIndicator type={action} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Field",
      key: "fieldName",
      sortable: true,
      render: ({ fieldName }) => (
        <ListTypography
          tooltip={fieldName?.replace("V.I.P.", "VIP") ?? ""}
          cellValue={fieldName?.replace("V.I.P.", "VIP")}
        />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Data",
      key: "newValue",
      sortable: true,
      csvValue: ({ newValue, fieldName }) =>
        getNewMonitoringSourceLabel(newValue, fieldName),
      render: ({ newValue, fieldName }) => (
        <ListTypography
          tooltip={getNewMonitoringSourceLabel(newValue, fieldName)}
          cellValue={getNewMonitoringSourceLabel(newValue, fieldName)}
        />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },

    {
      label: "Previous",
      key: "oldValue",
      sortable: true,
      csvValue: ({ oldValue, fieldName }) =>
        getNewMonitoringSourceLabel(oldValue, fieldName),
      render: ({ oldValue, fieldName }) => (
        <ListTypography
          tooltip={getNewMonitoringSourceLabel(oldValue, fieldName)}
          cellValue={getNewMonitoringSourceLabel(oldValue, fieldName)}
        />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Entity Type",
      key: "entityType",
      width: 160,
      sortable: true,
      render: (row) => <EntityTypeIndicator trail={row} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Entity ID",
      key: "entityId",
      tab: [TabValue.Export],
    },
  ];
};
